/* eslint-disable object-curly-newline */
import useMemoRef from '@platform/react/hook/useMemoRef'
import ErrorBoundary from 'ui/Error'
import { TextField } from '@mui/material'
import { useEffect, useState, useTransition } from 'react'
import PlatformEvent from 'lib/util/event'

/**
 * @typedef {Object} SelectOption
 * @property {string} key
 * @property {string} value
 */

/**
 * @callback ChangeHandler
 * @param {PlatformEvent} event
 */

/**
 * @callback GetSelection
 * @param {PlatformEvent} event
 * @returns Promise<SelectOption[]>
 */

/**
 * SelectField element
 *
 * Wrapper around {@link TextField} of `select` type intended to adapt the element's functionality
 * to the application's requirements and enable its use and management through its configuration
 * files.
 *
 * @see https://v5.mui.com/material-ui/api/text-field/#text-field-prop-select
 *
 * @param {React.Ref<any>} forwardedRef         forwarded reference to the root element
 * @param {SelectOption[]} [selection]          selectable items displayed by the element
 * @param {Object} [events]                     event handlers used by the element
 * @param {ChangeHandler} [events.onChange]     triggered whenever the selection changes
 * @param {GetSelection} [events.getSelection]  alternative way to fill `selection` on mount
 * @param {Object} [props]                      additional properties
 * @param {string} [props.value]                the element's current value
 * @param {string} [props.label]                text to be displayed as the element's label
 * @param {SelectOption[]} [props.options]      alias for `selection`
 * @param {boolean} [props.focused]             whether the element should be the focused one
 * @param {string|null} [props.helperText]      informative text to be displayed below the input
 * @param {string} [props.variant]              input style variant
 * @param {boolean} [props.error]               whether the input should appear in error state
 * @returns {JSX.Element}
 */
const SelectField = ({ forwardedRef, events, selection, ...props }) => {
  const { children, ...restProps } = props // we don't want the element to have any children
  // Make our input a controlled component
  // const [v, handleChange] = useHandleChange(value)
  const [, startTransition] = useTransition()
  const [value, setValue] = useState(props.value || '')
  const [options, setOptions] = useState(selection || props.options || [])
  const helperText = props.helperText === null ? null : props.helperText || '\u00A0'

  useEffect(() => {
    setValue(props.value || '')
  }, [props.value])

  useEffect(() => {
    setOptions(props.options || options)
  }, [props.options])

  useEffect(() => {
    (async () => {
      const newSelection = await events.getSelection?.(null)
      newSelection?.length && setOptions(newSelection)
    })()
  }, [])

  const handleChange = (event) => {
    startTransition(() => {
      const newValue = event.target.value
      setValue(newValue)
      // selecting a "blank" option passes '' as value, but we need an invalid value for this case
      events.onChange(new PlatformEvent(event, { value: newValue || null }))
    })
  }

  return (
    <ErrorBoundary>
      <TextField
        ref={forwardedRef}
        variant={'standard'}
        {...restProps}
        value={value}
        onChange={handleChange}
        SelectProps={{
          native: true,
        }}
        autoFocus={props.focused}
        helperText={helperText}
        fullWidth
        select
      >
        {options && options.map(item => (
          <option
            key={item.key}
            value={item.key}
          >
            {item.value}
          </option>
        ))}
      </TextField>
    </ErrorBoundary>
  )
}

export default useMemoRef(SelectField, props => [props.value, props.options, props.error])
